import { useSelector } from 'react-redux'
import { Text } from 'common-components'

import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import * as Styled from './styled'

import { translate } from 'utils/translations'
import { T } from 'consts/translationKeys'
import { memo, useEffect, useRef, useState } from 'react'
import OptionDropdown from './OptionDropdown'
import ImageAspectRatioDropdown from './ImageAspectRatioDropdown'
import { useActions } from './useActions'

const ModelSection = () => {
  const aiVideoModels = useSelector(aiVideoSelectors.aiVideoModels)
  const selectedModel = useSelector(aiVideoSelectors.selectedModel)
  const isNoModelSelected = useSelector(aiVideoSelectors.isNoModelSelected)
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false)
  const duration = useSelector(aiVideoSelectors.selectedDurationOption)
  const aiVideoAspectRatio = useSelector(aiVideoSelectors.aspectRatio)
  const defaultDimensions = useSelector(projectDataSelectors.defaultDimensions)
  const quality = useSelector(aiVideoSelectors.selectedQualityValue)
  const isFramesActiveAndFilled = useSelector(
    aiVideoSelectors.isFramesActiveAndFilled
  )

  const availableDurationOptions = useSelector(
    aiVideoSelectors.availableDurationOptions
  )
  const availableAspectRatioOptions = useSelector(
    aiVideoSelectors.availableAspectRatioOptions
  )
  const hasAspectRatioOptions = useSelector(
    aiVideoSelectors.hasAspectRatioOptions
  )
  const availableQualityOptions = useSelector(
    aiVideoSelectors.availableQualityOptions
  )

  const hasUserSelectedAspectRatio = useSelector(
    aiVideoSelectors.hasUserSelectedImageAspectRatio
  )

  const {
    handleModelChange,
    handleDurationChange,
    handleAspectRatioChange,
    handleImageAspectRatioChange,
    handleQualityChange
  } = useActions()

  const isFramesActiveAndFilledRef = useRef(isFramesActiveAndFilled)
  isFramesActiveAndFilledRef.current = isFramesActiveAndFilled

  const hasUserSelectedAspectRatioRef = useRef(hasUserSelectedAspectRatio)
  hasUserSelectedAspectRatioRef.current = hasUserSelectedAspectRatio

  useEffect(() => {
    if (
      defaultDimensions?.aspectRatioString &&
      !isFramesActiveAndFilledRef.current &&
      !hasUserSelectedAspectRatioRef.current
    ) {
      handleAspectRatioChange(defaultDimensions.aspectRatioString)
    }
  }, [defaultDimensions?.aspectRatioString, handleAspectRatioChange])

  return (
    <Styled.ModelSection>
      <Text type="h2" fontWeight="600" color="secondaryDark">
        {translate(T.MODEL_LABEL)}
      </Text>
      <Styled.DropdownWrapper>
        <Styled.VideoModelDropdownWrapper
          $pulse={isNoModelSelected && !isModelDropdownOpen}
        >
          <Styled.VideoModelDropdown
            models={aiVideoModels}
            selectedModelCode={selectedModel?.model ?? null}
            onModelSelect={handleModelChange}
            onOpenChange={setIsModelDropdownOpen}
          />
        </Styled.VideoModelDropdownWrapper>

        <Styled.ModelOptionsWrapper>
          <OptionDropdown
            className="option-dropdown"
            label={translate(T.DURATION_LABEL)}
            options={availableDurationOptions}
            selectedValue={duration?.value}
            onSelect={handleDurationChange}
            disabled={isNoModelSelected}
            disabledTooltipText={
              isNoModelSelected
                ? translate(T.SELECT_VIDEO_MODEL_TOOLTIP)
                : undefined
            }
            forceBottom
          />
          {hasAspectRatioOptions &&
            (isFramesActiveAndFilled ? (
              <ImageAspectRatioDropdown
                className="option-dropdown"
                options={availableAspectRatioOptions}
                selectedValue={aiVideoAspectRatio}
                onSelect={handleImageAspectRatioChange}
              />
            ) : (
              <OptionDropdown
                className="option-dropdown"
                label={translate(T.ASPECT_RATIO_LABEL)}
                options={availableAspectRatioOptions}
                selectedValue={aiVideoAspectRatio}
                onSelect={handleAspectRatioChange}
                disabled={isNoModelSelected}
                disabledTooltipText={
                  isNoModelSelected
                    ? translate(T.SELECT_VIDEO_MODEL_TOOLTIP)
                    : undefined
                }
              />
            ))}
          <OptionDropdown
            label={translate(T.QUALITY_LABEL)}
            options={availableQualityOptions}
            selectedValue={quality}
            onSelect={handleQualityChange}
            disabled={isNoModelSelected}
            disabledTooltipText={
              isNoModelSelected
                ? translate(T.SELECT_VIDEO_MODEL_TOOLTIP)
                : undefined
            }
            className="quality-dropdown"
          />
        </Styled.ModelOptionsWrapper>
      </Styled.DropdownWrapper>
    </Styled.ModelSection>
  )
}

export default memo(ModelSection)
